import PropTypes from 'prop-types';
import React from 'react';
import { getInputProps } from '@rjsf/utils';
import BaseObjectField from '@rjsf/core/lib/components/fields/ObjectField';
import { isBoolean, isEqual } from 'lodash';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import moment from 'moment-timezone';

import { DatePickerField, Input, PhoneInputField, SelectField } from '../Form';

export function CustomCheckbox(props) {
  const { id, label, schema, value, onChange, rawErrors, required, ...otherProps } = props;
  return (
    <Input
      id={id || label}
      data-testid={id || label}
      type="checkbox"
      label={schema?.description || label}
      checked={value}
      onChange={() => onChange(!value)}
      rowClassName="checkbox-row"
      errorsForTooltip={rawErrors?.length ? rawErrors : undefined}
      // workaround for form wanting required bool to be set to true
      required={required && !isBoolean(value)}
      {...otherProps}
    />
  );
};
CustomCheckbox.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  schema: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  rawErrors: PropTypes.array,
  required: PropTypes.bool,
};

export function CustomDateTime(props) {
  const { schema, value, onChange, rawErrors, label, id, ...otherProps } = props;
  return (
    <DatePickerField
      id={id || label}
      data-testid={id || label}
      label={schema.description || label}
      value={value ? moment(value) : undefined}
      onChange={date => onChange(date.format())}
      dateFormat="MM / DD / YYYY"
      peekNextMonth
      showMonthDropdown
      showYearDropdown
      className="drs-datepicker"
      errorsForTooltip={rawErrors?.length ? rawErrors : undefined}
      {...otherProps}
    />
  );
};
CustomDateTime.propTypes = {
  schema: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  rawErrors: PropTypes.array,
  label: PropTypes.string,
  id: PropTypes.string,
};

export function CustomSelect(props) {
  const { schema, options, value, onChange, rawErrors, label, id, ...otherProps } = props;
  return (
    <SelectField
      id={id || label}
      data-testid={id || label}
      type="select"
      label={schema.description || label}
      value={value}
      onChange={option => onChange(option.value)}
      data={options.enumOptions}
      errorsForTooltip={rawErrors?.length ? rawErrors : undefined}
      {...otherProps}
    />
  );
};
CustomSelect.propTypes = {
  schema: PropTypes.object,
  options: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  rawErrors: PropTypes.array,
  label: PropTypes.string,
  id: PropTypes.string,
};

export function CustomPhoneInput(props) {
  const { id, value, schema, required, onChange, rawErrors, label, ...otherProps } = props;
  const valueForInput = value || '';
  return (
    <PhoneInputField
      id={id || label}
      data-testid={id || label}
      name={id || label}
      align="left"
      label={schema.description || label}
      value={valueForInput}
      onChange={onChange}
      // isRequired={required}
      errorsForTooltip={rawErrors?.length ? rawErrors : undefined}
      rowClassName="phone-input-abs"
      {...otherProps}
    />
  );
};
CustomPhoneInput.propTypes = {
  id: PropTypes.string,
  schema: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  rawErrors: PropTypes.array,
  label: PropTypes.string,
};

export function BaseInputTemplate(props) {
  const {
    schema,
    id,
    options,
    label,
    value,
    type,
    placeholder,
    required,
    disabled,
    readonly,
    autofocus,
    onChange,
    onChangeOverride,
    onBlur,
    onFocus,
    rawErrors,
    hideError,
    uiSchema,
    registry,
    formContext,
    ...rest
  } = props;
  const onTextChange = ({ target: { value: val } }) => {
    // Use the options.emptyValue if it is specified and newVal is also an empty string
    onChange(val === '' ? options.emptyValue || '' : val);
  };

  const inputProps = { ...rest, ...getInputProps(schema, type, options) };
  const hasError = rawErrors?.length > 0 && !hideError;

  return (
    <Input
      id={id}
      label={schema.description || label}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readonly}
      // isRequired={required}
      error={hasError}
      errorsForTooltip={hasError ? rawErrors : undefined}
      onChange={onChangeOverride || onTextChange}
      data-testid={id}
      {...inputProps}
    />
  );
}
BaseInputTemplate.propTypes = {
  schema: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  options: PropTypes.object,
  label: PropTypes.string,
  value: PropTypes.any,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  autofocus: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onChangeOverride: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  rawErrors: PropTypes.array,
  hideError: PropTypes.bool,
  uiSchema: PropTypes.object,
  registry: PropTypes.object,
  formContext: PropTypes.object,
};

export function CustomFieldTemplate(props) {
  const { id, classNames, style, children, schema, hidden, uiSchema, formData } = props;
  const isLeaf = schema?.properties && !Object.values(schema?.properties).some(p => p.properties);

  const [expanded, setExpanded] = React.useState(uiSchema?.collapsible ? formData?.module_enabled : false);

  const content = (
    <>
      {uiSchema?.collapsible && (
        <div className="config-section-header" key={id}>
          <div className="buttonContainer">
            <button
              className={`minimize-button ${expanded ? '' : 'collapsed'}`}
              onClick={e => { setExpanded(!expanded); e.preventDefault(); }}
            />
          </div>
          <h3>{schema.description}</h3>
        </div>
      )}
      {uiSchema?.collapsible && !expanded ? null : children}
    </>
  );

  return id === 'root' ? (
    <OverlayScrollbarsComponent
      defer
      options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
      className="scrollbar-right-margin"
    >
      {content}
    </OverlayScrollbarsComponent>
  ) : (
    <div className={`${classNames}${isLeaf ? ' leaf' : ''}${hidden ? ' hidden' : ''}`} style={style}>
      {content}
    </div>
  );
}
CustomFieldTemplate.propTypes = {
  id: PropTypes.string.isRequired,
  classNames: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
  schema: PropTypes.object.isRequired,
  hidden: PropTypes.bool,
  uiSchema: PropTypes.object,
  formData: PropTypes.any,
};

const filterOrder = (properties, order) =>
  order.filter(prop => prop === '*' || properties.includes(prop))

// Helper function to recursively find all boolean field paths and array enum paths in a schema
const findBooleanPaths = (schema, path = '') => {
  const booleanPaths = [];
  if (path?.endsWith('module_superuser_only')) {
    return booleanPaths;
  }
  if (schema.type === 'boolean') {
    return [path];
  }
  
  // Handle array fields with enum options (rendered as checkboxes)
  if (schema.type === 'array' && schema.items?.type === 'string' && schema.items?.enum) {
    return [{ path, type: 'array-enum', enumOptions: schema.items.enum }];
  }
  
  if (schema.type === 'object' && schema.properties) {
    Object.keys(schema.properties).forEach(key => {
      const newPath = path ? `${path}.${key}` : key;
      const childPaths = findBooleanPaths(schema.properties[key], newPath);
      booleanPaths.push(...childPaths);
    });
  }
  
  if (schema.type === 'array' && schema.items && schema.items.type !== 'string') {
    // For arrays of objects, we'll need to handle this differently in the toggle function
    const childPaths = findBooleanPaths(schema.items, `${path}[]`);
    booleanPaths.push(...childPaths);
  }
  
  return booleanPaths;
};

// Helper function to get nested value from object using dot notation
const getNestedValue = (obj, pathOrField) => {
  // Handle array enum fields
  if (typeof pathOrField === 'object' && pathOrField.type === 'array-enum') {
    const value = pathOrField.path.split('.').reduce((current, key) => {
      return current?.[key];
    }, obj);
    return Array.isArray(value) ? value : [];
  }
  
  // Handle regular boolean paths
  return pathOrField.split('.').reduce((current, key) => {
    if (key.includes('[]')) {
      const arrayKey = key.replace('[]', '');
      return current?.[arrayKey] || [];
    }
    return current?.[key];
  }, obj);
};

// Helper function to set nested value in object using dot notation
const setNestedValue = (obj, pathOrField, value) => {
  // Handle array enum fields
  if (typeof pathOrField === 'object' && pathOrField.type === 'array-enum') {
    const keys = pathOrField.path.split('.');
    const lastKey = keys.pop();
    
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    
    // For array enums, set all enum options if true, empty array if false
    target[lastKey] = value ? [...pathOrField.enumOptions] : [];
    return;
  }
  
  // Handle regular boolean paths
  const keys = pathOrField.split('.');
  const lastKey = keys.pop();
  
  const target = keys.reduce((current, key) => {
    if (key.includes('[]')) {
      const arrayKey = key.replace('[]', '');
      if (!current[arrayKey]) current[arrayKey] = [];
      return current[arrayKey];
    }
    if (!current[key]) current[key] = {};
    return current[key];
  }, obj);
  
  if (lastKey.includes('[]')) {
    const arrayKey = lastKey.replace('[]', '');
    // For arrays, we need to handle each item
    if (Array.isArray(target)) {
      target.forEach(item => {
        if (typeof item === 'object') {
          item[arrayKey] = value;
        }
      });
    }
  } else {
    target[lastKey] = value;
  }
};

export function ObjectField({ uiSchema, ...props }) {
  const { schema, formData, onChange } = props;
  const properties = Object.keys(schema.properties)
  const order = uiSchema?.['ui:order']
  const showDisabledChildren = uiSchema?.['ui:options']?.showDisabledChildren;
  const moduleEnabled = properties?.includes('module_enabled') ? formData?.module_enabled : true;
  
  const booleanPaths = findBooleanPaths(schema);
  const hasBooleanFields = booleanPaths.length > 0;
  
  // Check if all boolean fields are currently true and all array enums are fully selected
  const allBooleansTrue = hasBooleanFields && booleanPaths.every(pathOrField => {
    const value = getNestedValue(formData, pathOrField);
    
    // For array enum fields, check if all enum options are selected
    if (typeof pathOrField === 'object' && pathOrField.type === 'array-enum') {
      const currentArray = Array.isArray(value) ? value : [];
      return pathOrField.enumOptions.every(option => currentArray.includes(option));
    }
    
    // For regular boolean fields
    return value === true;
  });
  
  // Function to toggle all boolean fields and array enums
  const toggleAllBooleans = (checked) => {
    const newFormData = { ...formData };
    
    booleanPaths.forEach(pathOrField => {
      setNestedValue(newFormData, pathOrField, checked);
    });
    
    onChange(newFormData);
  };
  
  // Count total toggleable fields for display
  const totalToggleableFields = booleanPaths.reduce((count, pathOrField) => {
    if (typeof pathOrField === 'object' && pathOrField.type === 'array-enum') {
      return count + 1; // Count array as one field
    }
    return count + 1; // Count boolean as one field
  }, 0);
  const newUISchema = { ...uiSchema, ['ui:order']: filterOrder(properties, ['module_enabled', 'module_superuser_only', 'module_available_till', '*'].concat(order || [])) }
  if (!moduleEnabled && showDisabledChildren) {
    properties?.forEach(prop => {
      if (prop !== "module_superuser_only" && prop !== "module_enabled") {
        newUISchema[prop] = { 'ui:disabled': true };
      }
    });
  }
  if (schema?.$id === 'https://git.rxcap.net/infrastructure/dsApi/org_user/types/org-config-request' && schema?.properties?.config?.properties) {
    newUISchema.permissions = newUISchema.permissions || {};
    newUISchema.config = newUISchema.config || {};
    Object.keys(schema?.properties?.config.properties)?.forEach(module => {
      const allowPermissionsWhenDisabled = newUISchema.permissions?.[module]?.['ui:options']?.showWhenDisabled;
      newUISchema.permissions[module] = {
        ...newUISchema.permissions[module],
      }
      if (!formData?.config?.[module]?.module_enabled && !allowPermissionsWhenDisabled) {
        newUISchema.permissions[module] = {
          'ui:widget': 'hidden',
        };
      }
      const moduleUiSchema = newUISchema.config[module] || {};
      newUISchema.config[module] = {
        ...moduleUiSchema,
        collapsible: typeof moduleUiSchema.collapsible === 'boolean' ? moduleUiSchema.collapsible : true,
        'ui:description': true,
        required: typeof moduleUiSchema.required === 'boolean' ? moduleUiSchema.required : false,
      };
    });
  }

  // const phoneProperties = properties.filter(prop => schema.properties[prop]?.type === 'string' && schema.properties[prop].format === 'phone');
  // phoneProperties.forEach(prop => {
  //   newUISchema[prop] = {
  //     'ui:widget': CustomPhoneInput,
  //     'ui:options': {
  //       inputType: 'tel',
  //     },
  //   };
  // });

  if (hasBooleanFields && newUISchema['ui:moduleLevel'] && totalToggleableFields > 1) {
    newUISchema['ui:toggleAllFields'] = {
      totalFields: totalToggleableFields,
      allSelected: allBooleansTrue,
      onToggle: toggleAllBooleans,
      fieldName: props.name,
      disabled: props.disabled,
    };
  }

  return <BaseObjectField {...props} uiSchema={newUISchema} />;
}

ObjectField.propTypes = {
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  formData: PropTypes.any,
  onChange: PropTypes.func.isRequired,
}

export function ArrayField(props) {
  const { schema, formData, items, onChange, canAdd, onAddClick, disabled } = props;

  const onCheckboxChange = element => {
    const newData = formData?.includes(element)
      ? formData.filter(item => item !== element)
      : [...(formData || []), element];
    onChange(newData);
  }

  const changeAll = (checked, elements) => {
    const newData = checked ? elements : [];
    onChange(newData);
  }

  return schema?.items?.type === 'string' ? (
    <fieldset>
      <div className="field-array-title">
        <CustomCheckbox
          key={schema.description}
          label={schema.description}
          value={isEqual(formData?.sort(), schema?.items?.enum?.sort())}
          onChange={checked => changeAll(checked, schema?.items?.enum)}
          disabled={disabled}
        />
      </div>
      <div className={`field-array-of-strings ${schema?.items?.enum?.length > 8 ? 'grid-view' : ''}`}>
        {schema?.items?.enum?.map(element => (
          <CustomCheckbox
            key={element}
            label={element}
            value={formData?.includes(element)}
            onChange={() => onCheckboxChange(element)}
            disabled={disabled}
          />
        ))}
      </div>
    </fieldset>
  ) : (
    <div>
      {items.map((element) => element.children)}
      {canAdd && <button type='button' onClick={onAddClick}>+</button>}
    </div>
  );
};
ArrayField.propTypes = {
  schema: PropTypes.object,
  formData: PropTypes.any,
  items: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  canAdd: PropTypes.bool,
  onAddClick: PropTypes.func,
  disabled: PropTypes.bool,
};

export function TitleFieldTemplate(props) {
  const { id, title, schema, uiSchema } = props;
  const toggleInfo = uiSchema?.['ui:toggleAllFields'];
  const shouldShowToggle = toggleInfo;
  const text = schema.description || title;
  const idValue = typeof id === 'string' ? id : '';
  const sectionMatch = idValue.match(/_(config|permissions)_(.+)__title$/);
  const sectionKey = sectionMatch?.[1] || '';
  const moduleKeyFromId = sectionMatch?.[2] || '';
  const moduleKey = toggleInfo?.fieldName || moduleKeyFromId || 'root';
  const toggleAllTestId = toggleInfo
    ? sectionKey === 'config'
      ? `toggle-all-fields-config-${moduleKey}`
      : `toggle-all-fields-${moduleKey}`
    : '';

  if (typeof uiSchema['ui:description'] === 'boolean' && !uiSchema['ui:description']) {
    return null;
  }
  return (
    <header id={id}>
      {text}
      {shouldShowToggle && (
        <CustomCheckbox
          id={`toggle-all-${toggleInfo.fieldName || 'root'}`}
          data-testid={toggleAllTestId}
          label={`Select all fields (${toggleInfo.totalFields} fields)`}
          value={toggleInfo.allSelected}
          onChange={toggleInfo.onToggle}
          schema={{ description: `${toggleInfo.allSelected ? 'Deselect' : 'Select'} all ${toggleInfo.totalFields} boolean and array fields in this section` }}
          disabled={toggleInfo.disabled}
        />
      )}
    </header>
  );
}
TitleFieldTemplate.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
};

export function DescriptionFieldTemplate() {
  return null;
}


